import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

type SettingsScope = "desktop" | "global";

interface DesktopSettingsState {
  theme: "dark" | "light" | "system";
  hideOnBlur: boolean;
  startPinned: boolean;
  autoSync: boolean;
  intervalSeconds: number;
}

interface GlobalSettingsState {
  language: string;
  defaultPage: "dashboard" | "plugins" | "scripts" | "files" | "clipboard";
  mobileCompactNav: boolean;
  inviteAutoExpireDays: number;
}

export default function Settings({ syncKey }: Props) {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [scope, setScope] = useState<SettingsScope>("desktop");
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");
  const [desktopSettings, setDesktopSettings] = useState<DesktopSettingsState>({
    theme: "dark",
    hideOnBlur: true,
    startPinned: false,
    autoSync: true,
    intervalSeconds: 300,
  });
  const [globalSettings, setGlobalSettings] = useState<GlobalSettingsState>({
    language: "en",
    defaultPage: "dashboard",
    mobileCompactNav: true,
    inviteAutoExpireDays: 7,
  });
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [pushSupported] = useState(() => "PushManager" in window && "serviceWorker" in navigator);
  const [pushStatus, setPushStatus] = useState<"idle" | "subscribed" | "error">("idle");
  const [apiToken, setApiToken] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const [ttlSec, setTtlSec] = useState(3600);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");
  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  const pushConfigured =
    !!vapidPublicKey &&
    vapidPublicKey.trim().length > 0 &&
    !vapidPublicKey.startsWith("REPLACE_WITH_");

  useEffect(() => {
    api.auth
      .me(syncKey)
      .then((r) => {
        setUsername(r.user.username ?? "");
        setName(r.user.name ?? "");
        setEmail(r.user.email ?? "");
      })
      .catch(() => {});

    api.sync
      .settings(syncKey)
      .then((r) => {
        const map: Record<string, string> = {};
        for (const item of r.items) map[item.key] = item.value;
        setSettings(map);

        const parseJson = <T,>(key: string, fallback: T): T => {
          const raw = map[key];
          if (!raw) return fallback;
          try {
            return { ...fallback, ...(JSON.parse(raw) as object) } as T;
          } catch {
            return fallback;
          }
        };

        setDesktopSettings(parseJson<DesktopSettingsState>("desktop_settings", {
          theme: "dark",
          hideOnBlur: true,
          startPinned: false,
          autoSync: true,
          intervalSeconds: 300,
        }));

        setGlobalSettings(parseJson<GlobalSettingsState>("global_settings", {
          language: "en",
          defaultPage: "dashboard",
          mobileCompactNav: true,
          inviteAutoExpireDays: 7,
        }));

        // Migrate legacy default page value
        setGlobalSettings((prev) => ({
          ...prev,
          defaultPage: prev.defaultPage === "scripts" ? "plugins" : prev.defaultPage,
        }));
      })
      .catch(() => {});
  }, [syncKey]);

  async function subscribePush() {
    if (!pushConfigured) {
      setPushStatus("idle");
      return;
    }

    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });
      await api.push.subscribe(syncKey, sub.toJSON());
      setPushStatus("subscribed");
    } catch {
      setPushStatus("error");
    }
  }

  async function generateApiToken() {
    setTokenBusy(true);
    try {
      const res = await api.auth.apiToken(syncKey, ttlSec);
      setApiToken(res.token);
    } catch {
      setApiToken("");
    } finally {
      setTokenBusy(false);
    }
  }

  async function saveProfile() {
    setProfileBusy(true);
    setProfileMsg("");
    try {
      const r = await api.auth.saveProfile(syncKey, { username, name, email });
      setUsername(r.user.username ?? "");
      setName(r.user.name ?? "");
      setEmail(r.user.email ?? "");
      setProfileMsg("Profile saved");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save profile";
      setProfileMsg(msg);
    } finally {
      setProfileBusy(false);
    }
  }

  async function saveScopedSettings() {
    setSettingsBusy(true);
    setSettingsMsg("");
    try {
      const item =
        scope === "desktop"
          ? { key: "desktop_settings", value: JSON.stringify(desktopSettings) }
          : { key: "global_settings", value: JSON.stringify(globalSettings) };

      await api.sync.saveSettings(syncKey, [item]);
      setSettings((prev) => ({ ...prev, [item.key]: item.value }));
      setSettingsMsg(`${scope === "desktop" ? "Desktop" : "Global"} settings saved`);
    } catch (err) {
      setSettingsMsg(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSettingsBusy(false);
    }
  }

  async function hardRefreshApp() {
    if (refreshBusy) return;
    setRefreshBusy(true);
    setRefreshMsg("Clearing cache and reloading...");

    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((reg) => reg.unregister()));
      }

      if ("caches" in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      }
    } catch {
      // Even if cleanup fails, still attempt a cache-busted reload.
    }

    const url = new URL(window.location.origin);
    url.searchParams.set("hard_refresh", Date.now().toString());
    window.location.replace(url.toString());
  }

  return (
    <div className="page">
      <h1>Settings</h1>

      <section className="settings-section">
        <h2>Sync Key</h2>
        <p>Sync key changes are managed by admin-issued keys and bundled desktop config.</p>
      </section>

      <section className="settings-section">
        <h2>Settings Scope</h2>
        <p>Choose whether you are editing desktop behavior or global account defaults.</p>
        <div className="key-change-row" style={{ gap: 8 }}>
          <button
            type="button"
            onClick={() => setScope("desktop")}
            style={{ opacity: scope === "desktop" ? 1 : 0.65 }}
          >
            Desktop Settings
          </button>
          <button
            type="button"
            onClick={() => setScope("global")}
            style={{ opacity: scope === "global" ? 1 : 0.65 }}
          >
            Global Settings
          </button>
        </div>

        {scope === "desktop" ? (
          <div className="key-change-row" style={{ flexWrap: "wrap", marginTop: 10 }}>
            <select
              value={desktopSettings.theme}
              onChange={(e) =>
                setDesktopSettings((prev) => ({
                  ...prev,
                  theme: e.target.value as DesktopSettingsState["theme"],
                }))
              }
            >
              <option value="dark">Theme: Dark</option>
              <option value="light">Theme: Light</option>
              <option value="system">Theme: System</option>
            </select>
            <label>
              <input
                type="checkbox"
                checked={desktopSettings.hideOnBlur}
                onChange={(e) =>
                  setDesktopSettings((prev) => ({ ...prev, hideOnBlur: e.target.checked }))
                }
              />{" "}
              Hide on blur
            </label>
            <label>
              <input
                type="checkbox"
                checked={desktopSettings.startPinned}
                onChange={(e) =>
                  setDesktopSettings((prev) => ({ ...prev, startPinned: e.target.checked }))
                }
              />{" "}
              Start pinned
            </label>
            <label>
              <input
                type="checkbox"
                checked={desktopSettings.autoSync}
                onChange={(e) =>
                  setDesktopSettings((prev) => ({ ...prev, autoSync: e.target.checked }))
                }
              />{" "}
              Auto sync
            </label>
            <input
              type="number"
              min={30}
              value={desktopSettings.intervalSeconds}
              onChange={(e) =>
                setDesktopSettings((prev) => ({
                  ...prev,
                  intervalSeconds: Math.max(30, Number(e.target.value) || 300),
                }))
              }
              placeholder="Sync interval (seconds)"
            />
          </div>
        ) : (
          <div className="key-change-row" style={{ flexWrap: "wrap", marginTop: 10 }}>
            <input
              type="text"
              value={globalSettings.language}
              onChange={(e) =>
                setGlobalSettings((prev) => ({ ...prev, language: e.target.value.slice(0, 12) }))
              }
              placeholder="Language (e.g. en)"
            />
            <select
              value={globalSettings.defaultPage}
              onChange={(e) =>
                setGlobalSettings((prev) => ({
                  ...prev,
                  defaultPage: e.target.value as GlobalSettingsState["defaultPage"],
                }))
              }
            >
              <option value="dashboard">Default page: Dashboard</option>
              <option value="plugins">Default page: Plugins</option>
              <option value="files">Default page: Files</option>
              <option value="clipboard">Default page: Clipboard</option>
            </select>
            <label>
              <input
                type="checkbox"
                checked={globalSettings.mobileCompactNav}
                onChange={(e) =>
                  setGlobalSettings((prev) => ({ ...prev, mobileCompactNav: e.target.checked }))
                }
              />{" "}
              Compact mobile nav
            </label>
            <input
              type="number"
              min={1}
              value={globalSettings.inviteAutoExpireDays}
              onChange={(e) =>
                setGlobalSettings((prev) => ({
                  ...prev,
                  inviteAutoExpireDays: Math.max(1, Number(e.target.value) || 7),
                }))
              }
              placeholder="Invite auto-expire days"
            />
          </div>
        )}

        <div className="key-change-row" style={{ marginTop: 10 }}>
          <button type="button" onClick={saveScopedSettings} disabled={settingsBusy}>
            {settingsBusy ? "Saving..." : `Save ${scope === "desktop" ? "Desktop" : "Global"} Settings`}
          </button>
        </div>
        {settingsMsg && <p>{settingsMsg}</p>}
      </section>

      <section className="settings-section">
        <h2>Sharing Identity</h2>
        <p>Used for trusted file invitations. Username is how others invite you.</p>
        <div className="key-change-row" style={{ flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input type="text" placeholder="name" value={name} onChange={(e) => setName(e.target.value)} />
          <input
            type="email"
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button type="button" onClick={saveProfile} disabled={profileBusy}>
            {profileBusy ? "Saving..." : "Save profile"}
          </button>
        </div>
        {profileMsg && <p>{profileMsg}</p>}
      </section>

      <section className="settings-section">
        <h2>API Access Token</h2>
        <p>Generate API keys/tokens for automation. Use as Bearer or X-API-Key.</p>
        <div className="key-change-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <input
            type="number"
            min={300}
            max={2592000}
            value={ttlSec}
            onChange={(e) => setTtlSec(Number(e.target.value) || 3600)}
            style={{ width: 180 }}
          />
          <button type="button" onClick={generateApiToken} disabled={tokenBusy}>
            {tokenBusy ? "Generating..." : "Generate token"}
          </button>
        </div>
        {apiToken && (
          <div className="key-change-row" style={{ marginTop: 8 }}>
            <textarea readOnly value={apiToken} style={{ width: "100%", minHeight: 96, fontFamily: "monospace" }} />
          </div>
        )}
      </section>

      <section className="settings-section">
        <h2>Push Notifications</h2>
        {!pushConfigured ? (
          <p>Push notifications are not configured yet.</p>
        ) : pushSupported ? (
          pushStatus === "subscribed" ? (
            <p className="success-text">Push notifications enabled for this browser.</p>
          ) : (
            <button type="button" onClick={subscribePush}>
              Enable push notifications
            </button>
          )
        ) : (
          <p>Push notifications are not supported in this browser.</p>
        )}
        {pushStatus === "error" && (
          <p className="error-text">Failed to subscribe. Check VAPID key configuration.</p>
        )}
      </section>

      <section className="settings-section">
        <h2>App Refresh</h2>
        <p>Force a hard refresh by clearing service worker/cache storage, then reloading this page.</p>
        <div className="key-change-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={hardRefreshApp} disabled={refreshBusy}>
            {refreshBusy ? "Refreshing..." : "Hard Refresh"}
          </button>
        </div>
        {refreshMsg && <p>{refreshMsg}</p>}
      </section>

      <section className="settings-section">
        <h2>Synced settings</h2>
        {Object.keys(settings).length === 0 ? (
          <p className="empty">No settings synced yet.</p>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(settings).map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td>
                    <code>{String(v).slice(0, 80)}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
